// Polaris React holdouts — Skeleton* used inside Suspense fallbacks (plan §10).
import { SkeletonBodyText, SkeletonDisplayText } from "@shopify/polaris";
import { Modal, TitleBar, useAppBridge } from '@shopify/app-bridge-react';
import { useFindMany, useGet, useFetch } from "@gadgetinc/react";
import { api } from "../api";
import { trackAdminUiEvent, ADMIN_UI_EVENTS } from "../utils/adminAnalytics";
import { fetchAnalyticsSummary, readAnalyticsSummary } from "../utils/analyticsSummary";
import { useDefaultTemplates } from "../hooks/useDefaultTemplates";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useContext, useState, useCallback, useRef, useMemo, lazy, Suspense } from "react";
// Direct imports for always-needed components.
// BootShell is the SAME picture index.html painted statically inside #root and that every
// gate above this one (router → auth → shop → route chunk) renders, so the last wait in
// the boot — this route's campaigns query — does not change the frame either.
import BootShell from "../components/BootShell";
// Lazy-load conditional/below-fold components to reduce homepage chunk size
const CampaignResourceList = lazy(() => import("../components/CampaignResourceList"));
const InsightsCard = lazy(() => import("../components/AiInsights/InsightsCard"));
const ErrorModal = lazy(() => import("../components/ErrorModal").then(m => ({ default: m.ErrorModal })));
const TemplateLibraryComponent = lazy(() => import("../components/TemplateLibrary").then(m => ({ default: m.TemplateLibrary })));
const AnalyticsSummaryCard = lazy(() => import("../components/Analytics/AnalyticsSummaryCard"));
const SetupCallModal = lazy(() => import("../components/SetupCallPrompt").then(m => ({ default: m.SetupCallModal })));

// App Home Components
import { SetupGuide } from "../components/SetupGuide";
import { TrialStatus } from "../components/TrialStatus";
import { SupportHub } from "../components/SupportHub";
import { WhatsNew } from "../components/WhatsNew";
import { useSetupCallPrompt } from "../hooks/useSetupCallPrompt";

// Module-level so the object identity is constant across renders. useFetch feeds its
// options through useStructuralMemo and then into the `send` callback's dep array, so a
// stable object keeps `send` stable — which is what makes it safe to depend on in the
// effect that triggers the trial-conversion fetch below.
const TRIAL_CONVERSION_FETCH_OPTIONS = { json: true, sendImmediately: false };

// Static changelog data
import changelogData from "../data/changelog.json";
import { useSearchParams } from "react-router-dom";
import { ShopContext } from "../providers/ShopProvider";
import { isTemplateLocked } from "../components/TemplateLibrary/lockedTemplates";
import { MERCHANT_VISIBLE_STATUSES } from "../utils/campaignFilters";
import { fetchPublishedDesign } from '../utils/templateDesign';
import { markBoot, measureDashboardBoot } from "../utils/bootTiming";
import PopupsPausedBanner from "../components/PopupsPausedBanner";
import { PLAN_CATALOGUE } from "../utils/planDisplayNames";

/**
 * Is this module running inside the campaign-editor iframe?
 *
 * The editor loads `/popup` in its own document, and that document evaluates the same
 * bundle. Warming sibling ADMIN routes from in there would download chunks the editor can
 * never navigate to, competing with the editor's own load. Mirrors the identically-named
 * constant in components/App.jsx, which guards its module-scope warm-ups for the same
 * reason — this route's warm-up lives in an effect, so it needs its own copy.
 */
const IS_CAMPAIGN_EDITOR =
  typeof window !== "undefined" && window.location.pathname === "/popup";

/**
 * Fetch a template's `settings` blob by id.
 *
 * WHY THIS EXISTS: `settings` is NOT on the catalogue record any more.
 * web/hooks/useDefaultTemplates.js drains through a projected Gelly view, and carrying
 * the whole blob there was measured SLOWER than the 5-round-trip cursor walk it replaced.
 * Only the four scalars anything READS are projected flat (displayType, industry,
 * position, overlayColor).
 *
 * But campaign / experiment-variant creation needs the blob VERBATIM — displayType,
 * position, teaser config and stopDisplaying all live in it. Copying `record.settings`
 * off a projected record would create the campaign successfully with `settings: {}` and
 * simply be wrong, with nothing failing. So the one template the merchant actually
 * clicked gets one light query on the click path.
 *
 * ⚠️ THROWS — and that is the contract, not an accident. It used to swallow everything and
 * return `{}`; see the long note in the body for why that was strictly worse. Two ways out:
 * `findOne` throws on a miss/network failure, and this function itself throws when the blob
 * comes back unusable. EVERY call site must therefore attach a rejection handler at the
 * moment the promise is created — not only at the `await` — because an overlapped fetch that
 * rejects before its await is reached is an UNHANDLED REJECTION, which is a page-level error
 * rather than a failed click. See the call site at the `settingsPromise` below.
 *
 * @param {string} templateId - campaign_template id
 * @param {Object} [fallback] - Value to try if the fetched record carries no settings
 * @returns {Promise<Object>} The settings blob — never null, never `{}`
 * @throws {Error} If the lookup fails, or resolves to anything that is not a plain object
 */
async function fetchTemplateSettings(templateId, fallback) {
  const fetched = await api.campaign_template.findOne(templateId, {
    select: { settings: true }
  });
  const settings = fetched?.settings || fallback;

  /**
   * ⚠️ THROW — DO NOT DEGRADE TO `{}`.
   *
   * This function used to `catch` and `return fallback || {}`. That looked defensive and was
   * the opposite: `fallback` is `template.settings`, and the whole point of the Gelly-view
   * change is that catalogue records NO LONGER CARRY `settings`, so the fallback is
   * `undefined` and the expression resolved to `{}`.
   *
   * A campaign created with `settings: {}` has no displayType, no position, no teaser config
   * and no stopDisplaying. It saves successfully, appears in the list, and is broken in a way
   * nobody sees until a shopper does. That is strictly worse than failing the click: a create
   * that errors is recoverable in one retry; a silently misconfigured campaign is not.
   *
   * Caught in pre-ship review. Both creation paths in this file and the one in
   * routes/templates.jsx had the same shape.
   */
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    throw new Error(
      `Template ${templateId} returned no settings — refusing to create a campaign with empty settings.`
    );
  }
  return settings;
}


export default () => {
  const shopify = useAppBridge();
  const { shop } = useContext(ShopContext);
  const navigate = useNavigate();

  // Popup quota for <PopupsPausedBanner>. `monthlyImpressionLimit` is NULL on every
  // pre-existing shop (field defaults are not backfilled — see billing.jsx), so fall
  // back to the catalogue entry for the shop's plan, the same way billing.jsx does.
  // A still-unknown limit stays undefined and the banner renders nothing.
  const popupLimit =
    shop?.monthlyImpressionLimit ??
    PLAN_CATALOGUE.find((p) => p.key === (shop?.currentPlan ?? "free"))?.impressionLimit;
  const popupCount = shop?.monthlyImpressionCount ?? 0;

  /**
   * Boot mark: the dashboard route's module has arrived and its component is running.
   *
   * ⚠️ IN THE RENDER BODY, NOT IN A useEffect — same reason, and the same precedent, as
   * `shop_ready` in providers/ShopProvider.jsx:117. An effect fires after paint, so it
   * would time the browser's paint rather than the route mounting, and every segment
   * anchored on it (`auth_to_dashboard_mount`, `campaigns_to_paint`) would be inflated by
   * that amount. markBoot is idempotent — first write wins, utils/bootTiming.js:69 — so
   * running on every render, including StrictMode's double invoke, is safe.
   *
   * Kept at the very top of the render body. It used to be load-bearing: the session hook
   * below SUSPENDED, so a mark placed after it timed the session round-trip instead of the
   * chunk — the opposite of what the route-chunk preload is trying to move. That hazard is
   * gone now (the session read no longer suspends, see below), but the position is still
   * the correct one and re-introducing a suspending hook above it would silently break the
   * measurement again.
   */
  markBoot("dashboard_mount");

  /**
   * NOT useSession(). That hook hard-codes `suspense: true`
   * (@gadgetinc/react/dist/esm/auth/useSession.js:16), so this line threw a promise and
   * ABORTED the render — the campaigns query further down could not even be ISSUED until
   * the session round-trip came back. Measured against production, a no-op Gadget query is
   * 258ms min / ~310ms median / 935ms worst over 10 samples, which matches the 320ms p50
   * recorded in docs/architecture/admin-load-time.md. That was a whole serial hop bought for
   * a value no first render uses.
   *
   * It CANNOT be un-suspended in place: the same hook does
   *     if (!session) throw new Error("currentSession not found but should be present");
   * so `useSession(undefined, { suspense: false })` turns the loading state into a THROWN
   * ERROR rather than an undefined session. Passing the option is worse than leaving it.
   *
   * useGet is the primitive useSession is built on, and it does NOT suspend unless asked.
   * ⚠️ The mechanism is not where it looks: urql reads `context.suspense`, NOT the top-level
   * flag (urql/dist/urql.js:353), and GadgetProvider sets `urqlClient.suspense = true`
   * GLOBALLY (GadgetProvider.js:35). What opts every other Gadget hook out is client-hooks'
   * useMemoizedQueryOptions, which writes `context.suspense = !!options?.suspense`
   * (createHooks.js:84-92) — an explicit `false` that beats the client default. So OMITTING
   * the option is a real opt-out, not a fallthrough. Do not "tidy" this by adding
   * `suspense: false`; that is the one spelling that is merely redundant.
   *
   * The select is narrower than useSession's on purpose: that hook pulls
   * api.currentSession.get.defaultSelection (__typename, id, createdAt, roles{key,name},
   * shopId, shopifySID, updatedAt — CurrentSession.js:2-9). Every consumer in this file
   * reads `session.id`, plus `session.shopId` for the campaigns query below.
   *
   * ⚠️ THE SCALAR `shopId`, NOT THE `shop` RELATIONSHIP. Both exist on the session
   * (AvailableSessionSelection, types.d.ts:12500-12501), but only `shopId` is in the
   * default selection — so `session?.shop?.id` on a select that never asked for `shop`
   * yields undefined and then FILTERS ON undefined rather than erroring, which is a
   * campaigns query that silently matches nothing. The scalar also costs no join.
   *
   * ⚠️ `session` is now legitimately undefined during the first paints. Every read of it
   * below sits in an async event handler or an effect and already guards on `session?.id` —
   * but those guards were DEAD CODE under suspense and are LIVE now. They are written as
   * `console.error(...); return;`, i.e. a merchant clicking a template in that window gets
   * nothing at all: no spinner, no error, no navigation. The window is small (the session
   * request now races the campaigns request, and the page does not paint until campaigns
   * land) but small is not zero.
   */
  const [{ data: session }] = useGet(api.currentSession, {
    select: { id: true, shopId: true },
  });

  /**
   * The shop id to query campaigns with, from whichever source answers FIRST.
   *
   * The boot used to be three strictly serial round trips — App Bridge auth → the shop
   * query in providers/ShopProvider.jsx → this route's campaigns query — because the
   * campaigns filter needed `shop.id` and nothing else could produce one. The session
   * carries the same id (see the `shopId` select above) and its request is already in
   * flight from the line above, so taking it when it wins collapses the last two hops
   * into one parallel pair. ShopProvider now mounts "/" early behind its cover, which is
   * what gives this a chance to run before `shop` lands.
   *
   * ⚠️ It is the SAME TENANT either way — the session belongs to the shop whose admin
   * embedded us — and this changes nothing about isolation regardless.
   * accessControl/filters/shopify/campaign.gelly scopes every read server-side, so this
   * filter is a narrowing convenience, not the security boundary.
   */
  const effectiveShopId = shop?.id ?? session?.shopId;
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const campaignListRef = useRef();
  // ⚠️ hasCampaigns / hasAnyCampaigns are DERIVED during render further down — they are
  // deliberately NOT stored in state behind a useEffect. These two hold only the
  // IMPERATIVE overrides ("I just created the first campaign", "I just deleted them
  // all"); `null` means "no override, use the derived value". See the derivation
  // below the campaigns query for why the effect form could strand the dashboard.
  const [hasCampaignsOverride, setHasCampaignsOverride] = useState(null);
  const [hasAnyCampaignsOverride, setHasAnyCampaignsOverride] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [currentModalCampaignId, setCurrentModalCampaignId] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [shouldOpenModal, setShouldOpenModal] = useState(false);

  // Save-in-flight indicator for the App Bridge TitleBar Save button.
  // The button lives outside the iframe; without this, clicking Save shows zero feedback
  // while the iframe performs the async save (1-5s).
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef(null);

  // Banner state for billing errors.
  // Shape: `{ message, heading?, action? }`. `heading` is optional and defaults at the two
  // render sites to 'Campaign Creation Failed', which is what almost every call site here
  // reports; createExperiment sets its own so the banner does not misname what failed.
  const [bannerError, setBannerError] = useState(null);

  /**
   * Resolve a session id for the async handlers below, fetching one if the render-time
   * query has not landed yet.
   *
   * ⚠️ THE `!session?.id` GUARDS BELOW WERE UNREACHABLE UNTIL 2026-08-16 AND ARE LIVE NOW.
   * While the session read suspended, `session` was always populated before any handler
   * could run, so every one of those guards was dead code — and every one of them is
   * written as `console.error(...); return;`. Live, that is a merchant clicking a template
   * tile in the first few hundred ms and NOTHING HAPPENING: no spinner, no error, no
   * navigation, in a production build where console.error is the only console call that
   * survives (.claude/rules/frontend.md). They click again.
   *
   * The window is small — the session request now races the campaigns request and the page
   * does not paint until campaigns land — but small is not zero, and one extra GET on a
   * click path is far cheaper than a dead button.
   *
   * Ref, not state: this is read only from async handlers and never rendered, so writing it
   * during render is the "latest value in a ref" pattern the frontend rules prescribe, and
   * it avoids an extra render.
   */
  const sessionIdRef = useRef(null);
  if (session?.id) sessionIdRef.current = session.id;
  const ensureSessionId = useCallback(async () => {
    if (sessionIdRef.current) return sessionIdRef.current;
    try {
      const fresh = await api.currentSession.get({ select: { id: true } });
      sessionIdRef.current = fresh?.id ?? null;
    } catch (err) {
      console.error("Failed to resolve current session:", err);
      sessionIdRef.current = null;
    }
    return sessionIdRef.current;
  }, []);

  // Defer non-critical queries until after initial render for faster first paint.
  //
  // ⚠️ DECLARED HERE, ABOVE ITS FIRST CONSUMER. The review-prompt effect below reads it in
  // a dependency array, which is evaluated during render at that point in the body — a
  // `const` declared further down would be in its temporal dead zone and throw.
  const [shouldFetchSecondary, setShouldFetchSecondary] = useState(false);

  // --- Native App Store review prompt (Pillar 1) ---
  // Fires Shopify's native review modal on dashboard load, but ONLY when the
  // server says it's a "happy moment" (>=1 conversion in the last 7d AND >=60d
  // since our last attempt). Shopify enforces the hard limits (60d/3x-year/
  // install<24h) and owns the modal — we never see the rating, so this is
  // ToS-compliant by construction. Must NOT be tied to a click (Shopify docs).
  //
  // DEFERRED: `checkReviewPromptEligibility` is a MUTATION round trip fired on every
  // dashboard mount, and its answer is "no" on almost every one of them — Shopify's own
  // 60-day / 3×-a-year / install<24h limits mean the modal can only appear a handful of
  // times in a shop's whole life. Paying for that on the first-paint critical path bought
  // nothing; 500 ms later is indistinguishable to the merchant.
  const reviewPromptAttempted = useRef(false);
  // True once Shopify's NATIVE review modal has been put on screen this session.
  //
  // ⚠️ `shopify.reviews.request()` resolves when the modal is SHOWN, not when it is
  // closed — so a merchant reading it for ten seconds is still looking at a host overlay
  // rendered OUTSIDE our iframe. Our own <s-modal> would open underneath it, invisibly,
  // and be gone from their point of view by the time they dismiss the review prompt.
  // Never reset: one interruption per session is the budget.
  const reviewModalShownRef = useRef(false);
  useEffect(() => {
    if (!shouldFetchSecondary) return;
    const shopId = shop?.id;
    if (!shopId || reviewPromptAttempted.current) return;
    if (!shopify?.reviews?.request) return; // App Bridge reviews API unavailable (older host)
    reviewPromptAttempted.current = true;
    (async () => {
      try {
        const elig = await api.shopifyShop.checkReviewPromptEligibility(shopId);
        if (!elig?.eligible) return;
        const res = await shopify.reviews.request();
        if (res?.success === true) reviewModalShownRef.current = true;
        await api.shopifyShop.recordReviewPromptOutcome(shopId, {
          outcome: res?.code ?? "declined-other",
          success: res?.success === true,
        });
      } catch (err) {
        // Non-critical: never disrupt the dashboard if the review flow fails.
        console.warn("[Nudgesmart] review prompt skipped:", err?.message);
      }
    })();
  }, [shouldFetchSecondary, shop?.id, shopify]);

  
  // Modal state for billing errors
  const [errorModal, setErrorModal] = useState({ visible: false, title: '', message: '', action: null });

  // Fetch theme extension status via app proxy
  const [themeExtensionData, setThemeExtensionData] = useState(null);
  const [fetchingThemeExtension, setFetchingThemeExtension] = useState(true);

  // Fetch theme extension status via centralized app proxy
  // Uses api.fetch() which handles authentication headers automatically
  //
  // ⚠️ EVERY `console.log` ON THIS PATH IS WRAPPED IN `import.meta.env.DEV`, and that is
  // not cosmetic. `vite.config.mjs` lists console.log/warn/info/debug in `esbuild.pure`,
  // which drops the CALL in a production build but KEEPS the argument expression — so
  // `console.log('Data:', JSON.stringify(data))` still serialised the whole (up to 1 MB)
  // theme settings payload on every production dashboard load, to throw it away. The
  // `import.meta.env.DEV` guard is statically false in production and takes the arguments
  // with it.
  const fetchThemeExtensionStatus = useCallback(async () => {
    if (import.meta.env.DEV) console.log('=== FRONTEND: Fetching theme extension status ===');
    setFetchingThemeExtension(true);
    try {
      const response = await api.fetch('/app-proxy', {
        method: 'POST',
        body: JSON.stringify({ action: 'get-theme-extension-status', payload: {} })
      });
      const data = await response.json();
      if (import.meta.env.DEV) {
        console.log('Response status:', response.status);
        console.log('=== FRONTEND: Raw API response ===');
        console.log('Data:', JSON.stringify(data));
        console.log('data.enabled:', data.enabled);
        console.log('typeof data.enabled:', typeof data.enabled);
      }
      setThemeExtensionData(data);
      if (import.meta.env.DEV) console.log('State updated with:', data);
    } catch (error) {
      console.error('Failed to fetch theme extension status:', error);
      setThemeExtensionData({ success: false, enabled: false });
    } finally {
      setFetchingThemeExtension(false);
    }
  }, []);

  // DEFERRED behind `shouldFetchSecondary`, not fired on mount.
  //
  // This one request is the most expensive thing the dashboard used to do before first
  // paint: it routes through `POST /app-proxy` — a 1,002-line route with 12+ helper
  // imports (discounts, spin results, scratch cards, surveys, social proof…) loaded to
  // answer one boolean — which then makes TWO SEQUENTIAL dependent Shopify Admin GraphQL
  // calls (~500 ms each) and parses the theme's whole `config/settings_data.json`
  // (200 kB – 1 MB).
  //
  // ⚠️ DEFER, DO NOT CACHE. `payload: {}` above carries no refresh flag, so any TTL turns
  // <SetupGuide>'s "Refresh" button into a no-op: the merchant enables the app embed, comes
  // back, clicks Refresh and is told it is still off — and that is the step that gates the
  // app working at all. Caching needs `payload: { refresh: true }` plumbed end-to-end first.
  useEffect(() => {
    if (!shouldFetchSecondary) return;
    fetchThemeExtensionStatus();
  }, [shouldFetchSecondary, fetchThemeExtensionStatus]);

  const hasThemeExtension = themeExtensionData?.enabled || false;

  // Debug: Log whenever themeExtensionData or hasThemeExtension changes
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log('=== FRONTEND: State changed ===');
    console.log('themeExtensionData:', themeExtensionData);
    console.log('hasThemeExtension:', hasThemeExtension);
    console.log('===============================');
  }, [themeExtensionData, hasThemeExtension]);

  // Emit the onboarding milestone only on a GENUINE false -> true transition.
  //
  // ⚠️ A bare `useRef(false)` guard does NOT mean "once per shop" — it means "once per
  // MOUNT", and the previous version claimed the former. `hasThemeExtension` derives from
  // the DEFERRED fetchThemeExtensionStatus() call, so it is false on mount and flips true a
  // moment later on EVERY load. Every merchant who already had the extension on therefore
  // emitted a fresh "milestone" every single time they opened the dashboard — inflating the
  // GA4 onboarding funnel with events that never happened, and billing for each one.
  //
  // The fix is a baseline seeded from the first SETTLED value: an already-enabled extension
  // is recorded silently, and only a real off -> on transition (merchant enables it, clicks
  // SetupGuide's Refresh) emits. `themeExtensionData` is null until settled and the catch
  // path sets { success: false, enabled: false }, so a failed fetch still settles rather
  // than wedging the guard shut forever.
  //
  // Known tradeoff: enabling the extension and then FULL-PAGE-RELOADING (instead of clicking
  // Refresh) re-seeds the baseline as already-true and emits nothing. That is strictly better
  // than the old behaviour of emitting on every load.
  const themeExtensionBaselineRef = useRef(null); // null = no settled value yet
  useEffect(() => {
    if (themeExtensionData === null) return;              // not settled — never seed on null
    if (themeExtensionBaselineRef.current === null) {
      themeExtensionBaselineRef.current = hasThemeExtension; // seed silently
      return;
    }
    if (hasThemeExtension && themeExtensionBaselineRef.current === false) {
      themeExtensionBaselineRef.current = true;
      trackAdminUiEvent(api, ADMIN_UI_EVENTS.THEME_EXTENSION_ENABLED, {});
    }
  }, [themeExtensionData !== null, hasThemeExtension]);   // primitives only, per CLAUDE.md

  // Callback to refresh extension status
  const handleRefreshExtensionStatus = useCallback(() => {
    fetchThemeExtensionStatus();
  }, [fetchThemeExtensionStatus]);

  // Check for any campaigns including experiments to determine dashboard vs welcome page
  // Also used to compute campaign counts for the Campaign Summary sidebar
  //
  // `pause` — NOT `enabled`. `enabled` is not an option in @gadgetinc/client-hooks;
  // it is silently dropped and the query fires anyway. `pause` is the real gate, but it
  // also SUPPRESSES the error and settles as { data: undefined, fetching: false }, which
  // is why nothing below may wait on an effect to learn the answer (see the derivation).
  //
  // ⚠️ KEYED ON `effectiveShopId`, NOT `shop?.id` — see its declaration above. Both the
  // filter and the `pause` must use it, or the query pauses on one id and filters on
  // another and never runs at all.
  const [{ data: anyCampaignsData, fetching: fetchingAnyCampaigns, error: anyCampaignsError }, checkAnyCampaigns] = useFindMany(api.campaign, {
    filter: {
      shopId: { equals: effectiveShopId },
      status: { in: MERCHANT_VISIBLE_STATUSES }
    },
    select: { id: true, status: true },
    first: 250,
    pause: !effectiveShopId
  });

  /**
   * Start the Analytics Overview request HERE, beside the campaigns query — not from the
   * card's mount effect.
   *
   * `GET /shop-analytics` resolves the shop from the SESSION
   * (`connections.shopify.currentShopId`, api/routes/GET-shop-analytics.js) and never reads a
   * shop id off the request. The `shopId` prop on <AnalyticsSummaryCard/> was therefore only
   * ever a readiness gate — its own comment says so — and firing from that component made the
   * request wait on four things it does not need: the shop query, the campaigns query, the
   * 500 ms idle callback below, and the card's own lazy chunk.
   *
   * ⚠️ MEASURED against production 2026-08-18, so the shape of the win is not a guess:
   * `/shop-analytics?summary=true` is ~470 ms warm / ~670 ms with all three HogQL queries
   * live, against ~498 ms for `/debug` — a route that does NOTHING. The PostHog work is
   * ~180 ms of it. The request was never the slow part; the 1-4 s of waiting in front of it
   * was. First-ever calls hit a cold route worker at 15 s and 41 s, which is the case this
   * helps most: started early it overlaps the rest of the boot instead of following it.
   *
   * ⚠️ Keyed on `effectiveShopId`, matching the campaigns query above — same signal, same
   * reason. `shop?.id` alone would reinstate the shop-query wait (320 ms p50 / 2.1 s p90)
   * that this exists to remove.
   *
   * ⚠️ `shouldFetchSecondary` still gates the RENDER further down, so the card's chunk stays
   * on the deferred tier and the first-paint budget is unchanged. Only the network moved.
   *
   * ⚠️ Fire-and-forget by design, and the `.catch` is NOT swallowing a real failure —
   * `fetchAnalyticsSummary` drops its own cache entry on rejection, and the card calls it
   * again on mount, where the error reaches the banner. Without the catch this is an
   * unhandled rejection in the console on every offline boot.
   *
   * ⚠️ A merchant who turns out to be PAYWALLED now issues one request they did not before,
   * because "/" is in EARLY_MOUNT_PATHS and mounts ahead of the paywall decision. That is the
   * same trade the campaigns query above already makes, at ~180 ms of PostHog work against a
   * 1,200/hour ceiling.
   */
  useEffect(() => {
    if (!effectiveShopId) return;
    fetchAnalyticsSummary(effectiveShopId).catch(() => {});
  }, [effectiveShopId]);

  /**
   * Boot mark: the campaigns query has landed, i.e. there is finally something real to
   * paint. Render body and idempotent, for the same reasons as `dashboard_mount` above.
   *
   * ⚠️ Keyed on the DATA, not on `!fetchingAnyCampaigns`. A paused Gadget/urql query
   * settles as { data: undefined, fetching: false } (see the derivation note below), so a
   * fetching-based mark would fire while the query had not even been issued — timing the
   * shop query's absence rather than the campaigns query's arrival.
   */
  if (anyCampaignsData) markBoot("campaigns_ready");

  /**
   * Emit the dashboard boot measures.
   *
   * ⚠️ THIS CALL IS THE WHOLE INSTRUMENT. measureDashboardBoot() existed in
   * utils/bootTiming.js with nothing calling it, which means every mark above was being
   * recorded and NO measure was ever produced — `performance.getEntriesByType('measure')`
   * came back empty, which is indistinguishable from "the marks are broken". Marks alone
   * are not enough; popup.jsx:160 carries the same warning for the editor's copy.
   *
   * In an effect, not the render body: it reads marks that the render body has only just
   * written, and measureBoot() calls performance.measure(), which THROWS a SyntaxError on
   * a missing mark — bootTiming catches that and returns null, but a render-phase call
   * would still be doing I/O-shaped work during render for no benefit.
   *
   * Ref-guarded because `live: true` and urql's cache-and-network default both re-issue
   * this query after first paint: without the guard every background refetch would append
   * a duplicate set of ns:* measures and the timeline would stop being readable.
   */
  const bootMeasuredRef = useRef(false);
  useEffect(() => {
    if (!anyCampaignsData || bootMeasuredRef.current) return;
    bootMeasuredRef.current = true;
    measureDashboardBoot();
  }, [anyCampaignsData]);

  // Compute campaign counts for Campaign Summary sidebar
  const campaignCounts = useMemo(() => {
    if (!anyCampaignsData) return { active: 0, draft: 0, total: 0 };
    const active = anyCampaignsData.filter(c => c.status === 'active').length;
    const draft = anyCampaignsData.filter(c => c.status === 'draft').length;
    return { active, draft, total: anyCampaignsData.length };
  }, [anyCampaignsData]);

  // ---------------------------------------------------------------------------
  // "Does this shop have campaigns?" — DERIVED DURING RENDER, never effect-then-state.
  //
  // This used to be two useState(null)s written by a useEffect guarded on
  // `if (anyCampaignsData && !fetchingAnyCampaigns)`. With a PAUSED query that guard
  // can never be true — a paused Gadget/urql query settles as
  // { data: undefined, fetching: false, error: undefined } — so hasAnyCampaigns stayed
  // `null` and the render gate at the bottom of this file returned <BootShell/>
  // FOREVER, with no error anywhere. Deriving in render removes the `null` limbo: the
  // value resolves the instant the query settles in ANY way (data, error, or paused),
  // so there is no state the dashboard can get stuck in.
  //
  // `null` here means only "the request is genuinely in flight".
  //
  // ⚠️ A FAILED QUERY MUST NOT RESOLVE TO `false`. `false` is what renders the
  // first-run Welcome page + template picker. Telling a shop that HAS campaigns it
  // has none — because one request failed — invites it to build a duplicate. The
  // error gets its own terminal branch below the derivation instead; see the early
  // return above the skeleton gate.
  const derivedHasCampaigns = useMemo(() => {
    if (anyCampaignsData) return anyCampaignsData.length > 0;
    if (fetchingAnyCampaigns) return null;
    // Paused (no shop identity at all) is terminal and genuinely means "nothing to show".
    // It is what keeps `null` from being a permanent state.
    //
    // ⚠️ `effectiveShopId`, to match the `pause` above — anything else describes a
    // different query than the one that is running. This branch is REACHABLE now:
    // ShopProvider mounts "/" early (EARLY_MOUNT_PATHS), so the first render happens
    // with neither `shop` nor `session` landed. The render gate at the bottom of this
    // file holds the skeleton until `shop` arrives, so the merchant never sees the
    // Welcome page this would otherwise resolve to.
    if (!effectiveShopId) return false;
    return null;
  }, [anyCampaignsData, fetchingAnyCampaigns, effectiveShopId]);

  // An imperative override wins over the derived value — same precedence the old
  // setState-after-the-effect had. Set by SAVE_COMPLETE / CONFIRM_CANCEL /
  // createCampaignFromTemplate / createExperiment (first campaign → true).
  //
  // ⚠️ CLEAR TO `null`, NEVER WRITE `false`. Nothing else resets these, so a stale
  // override outlives the data it was standing in for. `onAllDeleted` writing
  // `false` was a bug in exactly one direction: deleting the last campaign left
  // `hasAnyCampaignsOverride` pinned `true` from the create, so the dashboard could
  // never fall back to the Welcome page even though the refetch had returned [].
  // Clearing hands control back to the derived value, which the delete's own
  // refetch has already updated (useAction injects additionalTypenames:["Campaign"],
  // so urql's cacheExchange invalidates this query).
  const hasCampaigns = hasCampaignsOverride ?? derivedHasCampaigns;
  const hasAnyCampaigns = hasAnyCampaignsOverride ?? derivedHasCampaigns;

  // Deferred: templates load after campaigns query completes (500ms delay via shouldFetchSecondary)
  // Used by createCampaignFromTemplate and createExperiment
  //
  // SHARED DRAIN: this route runs its own template query AND renders <TemplateLibrary>
  // further down (which needs the same list), so the dashboard used to pull the whole
  // 1,200-record catalogue twice concurrently. useDefaultTemplates dedupes them through a
  // module-level cache — a React context could not, because this call sits ABOVE
  // <TemplateLibrary> in the tree. The light select (no ~40 KB element trees) now lives
  // in that hook; nothing here renders a tree — createCampaignFromTemplate fetches the
  // one it needs by id (see below).
  /**
   * ⚠️ NARROWED to the states that actually consume it. Measured on production
   * 2026-08-17: CATALOGUE_VIEW returns 1,173 rows / 349 KB, Gadget does NOT cache views
   * server-side (it recomputes per request, against a 60s-per-10s-wall-clock budget per
   * environment), and it used to fire on EVERY dashboard load.
   *
   * <TemplateLibraryComponent> renders only on the no-campaigns branch further down, so for
   * an established merchant — the common case — all 349 KB were drained for nothing.
   *
   * The `create-experiment` clause is REQUIRED, not belt-and-braces: the effect that handles
   * that deep link is guarded on `&& templateData`, so without it the link would silently
   * no-op on a shop that has campaigns.
   *
   * Narrowing this costs the library nothing — useTemplateLibrary calls useDefaultTemplates
   * itself, and `enabled` gates the NETWORK, not the data, so this hook still picks the rows
   * up from the module cache once the library drains them.
   */
  const needsCatalogue =
    hasAnyCampaigns === false || searchParams.get('action') === 'create-experiment';

  const { data: templateData, fetching: fetchingTemplates } = useDefaultTemplates({
    enabled: shouldFetchSecondary && needsCatalogue
  });

  // Check if shop has email integrations connected.
  // `select: { id: true }` — the only consumer is the `> 0` count below, and without a
  // select this pulled every column including the whole `config` JSON blob.
  // Filter on the FK (`shopId`) rather than traversing the relationship (`shop: { id: … }`);
  // every other query in web/ uses the FK form.
  // `effectiveShopId`, not `shop?.id` — the shop query settles ~400ms into the boot but this
  // used to wait for it anyway, serialising a 320ms query behind another one for no reason.
  // The session read resolves the same id earlier, and the route derives the shop server-side
  // from connections.shopify.currentShopId regardless, so this changes WHEN the request
  // fires, never what it returns. Same rationale as the campaigns query above.
  const [{ data: integrationsData, fetching: fetchingIntegrations }] = useFindMany(api.emailProviderConfiguration, {
    filter: {
      shopId: { equals: effectiveShopId }
    },
    select: { id: true },
    first: 1,
    pause: !effectiveShopId
  });

  // Real conversion count for <TrialStatus> (see GET-trial-conversion-count.js).
  //
  // This replaces `shop.monthlyEmailCaptures`, a field that has NEVER existed on
  // shopifyShop. Read as `shop.monthlyEmailCaptures || 0`, the missing field silently
  // became 0 rather than throwing, so a merchant with 16 real conversions was shown
  // "Don't lose your 0 leads". Do not reintroduce that read.
  //
  // Deferred behind shouldFetchSecondary so it stays off the first-paint critical path
  // (cut 654KB→233KB gz in 78375e31 — do not regress it).
  //
  // ⚠ GATING: the three deferral mechanisms in this file are NOT interchangeable.
  //     Gadget data hooks  -> `pause`   (see the emailProviderConfiguration query above)
  //     this repo's hooks  -> `enabled` (see useDefaultTemplates above — correct there)
  //     useFetch           -> `sendImmediately: false` + call send() yourself
  //
  // Do NOT gate useFetch by passing a null URL. That idiom appears in
  // components/AudienceTargeting.jsx:105-127, but it does not work: useFetch has no
  // null-path guard, and `startRequestOnMount` defaults to true for GET, so it calls
  // connection.fetch(null) on mount regardless. Its own type signature is
  // `(path: string, …)`, not `string | null` — nothing catches the misuse because this
  // repo has no tsconfig. Verified by reading @gadgetinc/client-hooks/dist/esm/useFetch.js.
  //
  // `send` is safe in the dep array below: its useCallback deps are
  // [connection, memoizedOptions, path], and memoizedOptions comes from
  // useStructuralMemo, so a structurally-constant options object keeps it stable.
  const [{ data: trialConversionData }, fetchTrialConversions] = useFetch(
    '/trial-conversion-count',
    TRIAL_CONVERSION_FETCH_OPTIONS
  );

  /**
   * The ONE condition deciding whether <TrialStatus> exists — used by the fetch gate below
   * AND by both render sites further down (the has-campaigns and no-campaigns branches).
   *
   * ⚠️ Derived once on purpose. The fetch used to gate on `shouldFetchSecondary && shop?.id`
   * while the render gated on this, so every shop that never shows the card still paid for
   * the request (~486ms measured on production). Two copies of a condition drift; this one
   * had already drifted. Keep all three sites reading this const.
   */
  const showTrialStatus = Boolean(
    shop?.trialStartedAt &&
    shop?.currentPlan &&
    shop?.currentPlan !== 'free' &&
    ['active', 'pending'].includes(shop?.subscriptionStatus)
  );

  useEffect(() => {
    if (!shouldFetchSecondary || !showTrialStatus) return;
    // Errors surface through the hook's own state; TrialStatus falls back to 0, which is
    // exactly the pre-fix behaviour. Never let this reject unhandled.
    void fetchTrialConversions().catch(() => {});
  }, [shouldFetchSecondary, showTrialStatus, fetchTrialConversions]);

  const trialConversions = trialConversionData?.conversions ?? 0;

  // Check if shop has any active campaigns (for setup guide)
  const hasActiveCampaign = useMemo(() => {
    return anyCampaignsData?.some(campaign => campaign.status === 'active') || false;
  }, [anyCampaignsData]);

  // Check if shop has email integration connected
  const hasEmailIntegration = useMemo(() => {
    return (integrationsData?.length || 0) > 0;
  }, [integrationsData]);

  // Have ALL of <SetupGuide>'s derived inputs resolved?
  //
  // SetupGuide diffs completedSteps against a seed taken on its first effect run, so that a
  // returning merchant's already-finished steps do not fire as fresh completions. That seed
  // is only meaningful once every input has settled — and they do NOT settle together.
  // hasThemeExtension and hasEmailIntegration both arrive from DEFERRED fetches, i.e. AFTER
  // the seed ran, so each transitioned false -> true post-seed and emitted a phantom
  // ns_onboarding_step_completed on every dashboard mount.
  //
  // ⚠️ KEYED ON THE DATA, NOT ON THE `fetching` FLAGS. The integrations query is
  // `pause: !effectiveShopId`, and a PAUSED Gadget/urql query settles as
  // { data: undefined, fetching: false } — indistinguishable from "finished and empty".
  // Reading !fetchingIntegrations would report "settled" for a query that never ran and
  // rebuild the exact bug this guard removes. Same reasoning as the markBoot guard above.
  //
  // anyCampaignsData needs no entry: the parent early-returns a skeleton until it resolves,
  // so it is always settled before <SetupGuide> mounts.
  const setupInputsSettled =
    themeExtensionData !== null && Array.isArray(integrationsData);

  // Add message listener for modal communication
  useEffect(() => {
    function handleMessageFromModal(event) {
      // Only handle messages from our modal
      if (event.origin !== location.origin) return;
      
      const { type, payload } = event.data || {};


      // Any reply that means "the save attempt is over" should clear the spinner.
      const SAVE_TERMINAL_TYPES = ['SAVE_COMPLETE', 'CAMPAIGN_UPDATED', 'SAVE_ERROR', 'SAVE_BLOCKED'];
      if (SAVE_TERMINAL_TYPES.includes(type)) {
        setIsSaving(false);
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
          saveTimeoutRef.current = null;
        }
      }

      switch (type) {
        case 'SAVE_COMPLETE':
          console.log('🔔 Parent received SAVE_COMPLETE:', payload);

          shopify.modal.hide('my-modal');
          localStorage.removeItem('currentCampaignId');
          localStorage.removeItem('currentExperienceId'); // Also clean up experience ID
          setCurrentModalCampaignId(null);
          (async () => {
            await new Promise(r => setTimeout(r, 2000));
            setRefreshTrigger(Date.now());
            try {
              await campaignListRef.current?.refetchCampaigns();
            } catch (error) {
              console.error('Error refreshing campaigns after save:', error);
            }
            if (!hasCampaigns) {
              setHasCampaignsOverride(true);
              setHasAnyCampaignsOverride(true); // Also update hasAnyCampaigns
            }
          })();
          break;
        case 'SAVE_ERROR':
          console.log('🔔 Parent received SAVE_ERROR:', payload);
          console.error('Save failed:', payload?.error);
          break;
        case 'SAVE_BLOCKED':
          // Save was intercepted by an in-iframe modal (e.g., rules-warning).
          // The spinner was already cleared by the terminal-types guard above; no further action needed.
          console.log('🔔 Parent received SAVE_BLOCKED:', payload);
          break;
        case 'CAMPAIGN_UPDATED':
          console.log('🔔 Parent received CAMPAIGN_UPDATED:', payload);
          // Refresh campaign list without closing modal
          (async () => {
            setRefreshTrigger(Date.now());
            try {
              await campaignListRef.current?.refetchCampaigns();
              console.log('✅ Campaign list refreshed after update');
            } catch (error) {
              console.error('Error refreshing campaigns after update:', error);
            }
            if (!hasCampaigns) {
              setHasCampaignsOverride(true);
              setHasAnyCampaignsOverride(true);
            }
          })();
          break;
        case 'LEAVE_EDITOR_FOR': {
          /**
           * The editor asked to hand the merchant off to a real app page — see
           * utils/leaveCampaignEditor.js for why navigating inside the modal cannot work.
           * Close the modal FIRST, then navigate: leaving it open would stack the destination
           * page behind a modal whose Save/Cancel belong to an editor that is gone.
           */
          const nextPath = payload?.path;
          // Only app-relative routes. This value crossed a frame boundary, and an absolute
          // URL here would make the handler an open redirect.
          if (typeof nextPath !== 'string' || !nextPath.startsWith('/') || nextPath.startsWith('//')) {
            console.warn('Ignoring LEAVE_EDITOR_FOR with a non-relative path:', nextPath);
            break;
          }
          shopify.modal.hide('my-modal');
          localStorage.removeItem('currentCampaignId');
          localStorage.removeItem('currentExperienceId');
          setCurrentModalCampaignId(null);
          navigate(nextPath);
          break;
        }
        case 'CONFIRM_CANCEL':
          console.log('🔔 Parent received CONFIRM_CANCEL:', payload);
          // User confirmed cancel (either saved or discarded changes)
          shopify.modal.hide('my-modal');
          localStorage.removeItem('currentCampaignId');
          localStorage.removeItem('currentExperienceId');
          setCurrentModalCampaignId(null);
          if (!hasCampaigns) {
            setHasCampaignsOverride(true);
            setHasAnyCampaignsOverride(true);
          }
          setTimeout(async () => {
            setRefreshTrigger(Date.now());
            try {
              await campaignListRef.current?.refetchCampaigns();
            } catch (error) {
              console.error('Error refreshing campaigns after cancel:', error);
            }
          }, 2000);
          break;
      }
    }

    window.addEventListener('message', handleMessageFromModal);
    return () => {
      window.removeEventListener('message', handleMessageFromModal);
    };
  }, [shopify.modal, shop, campaignListRef, hasCampaigns, navigate]);
  
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const handleSaveCampaign = () => {
    // Early-return: never start a second save while one is in flight.
    if (isSaving) return;
    setIsSaving(true);

    // Safety net: if no reply arrives within 30s (network failure, dropped message,
    // iframe gone), force-clear the spinner so the user can retry.
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      console.warn('[index.jsx] Save spinner timeout (30s) — clearing isSaving as safety net');
      setIsSaving(false);
      saveTimeoutRef.current = null;
    }, 30000);

    const modal = document.getElementById('my-modal');
    modal?.contentWindow?.postMessage({ type: 'SAVE_CAMPAIGN' }, location.origin);
  };

  const handleTabChange = useCallback(
    (selectedTabIndex) => setSelected(selectedTabIndex),
    [],
  );

  function openModal(campaignId) {
    try {
      if (campaignId) {
        // Set the campaign ID in localStorage and state
        localStorage.setItem('currentCampaignId', campaignId);
        setCurrentModalCampaignId(campaignId);
        
        // Always defer modal opening to ensure the modal component is rendered
        setShouldOpenModal(true);
      }
    } catch (error) {
      console.error('Error opening modal:', error);
    }
  }
  
  // Initialize currentModalCampaignId from localStorage on mount
  useEffect(() => {
    const campaignIdFromStorage = localStorage.getItem('currentCampaignId');
    if (campaignIdFromStorage) {
      setCurrentModalCampaignId(campaignIdFromStorage);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Sidekick intent landing — `application/campaign` + `edit`
  //
  // Declared AFTER the localStorage restore above ON PURPOSE. React runs effects in
  // declaration order, and that restore is unguarded: if this ran first, the stale stored
  // id would overwrite the deep-linked one and the merchant would silently open the WRONG
  // campaign. See extensions/sidekick-open-campaign/shopify.extension.toml.
  //
  // Kept as its OWN effect rather than folded into the `?action=create-experiment` effect
  // below, which calls setSearchParams({}) — a total wipe that would delete this param
  // before it could be read. Same collision the comment at templates.jsx:283-285 warns about.
  // ---------------------------------------------------------------------------
  const sidekickIntentHandledRef = useRef(false);

  useEffect(() => {
    // Fire once per mount. The effect below re-runs whenever searchParams identity changes,
    // and openModal() writes to localStorage, so a second pass could reopen the modal.
    if (sidekickIntentHandledRef.current) return;

    const rawCampaignId = searchParams.get('nsCampaignId');
    if (!rawCampaignId || !shop?.id) return;

    sidekickIntentHandledRef.current = true;

    // Sidekick passes a GID (gid://application/campaign/123); the raw id is the tail.
    const campaignId = String(rawCampaignId).split('/').pop();

    let cancelled = false;

    (async () => {
      try {
        // Verify the campaign belongs to THIS shop before opening anything. The id arrives
        // from outside the app, so it is untrusted input.
        const matches = await api.campaign.findMany({
          filter: { id: { equals: campaignId }, shopId: { equals: shop.id } },
          first: 1,
          select: { id: true },
        });

        if (cancelled) return;

        if (!matches?.[0]) {
          await shopify?.intents?.response?.error?.("That campaign isn't in this store.");
          return;
        }

        openModal(campaignId);
        // The intent's job is to get the merchant to the right place; that is now done.
        await shopify?.intents?.response?.ok?.({ campaignId });
      } catch (error) {
        if (cancelled) return;
        console.error('Error handling Sidekick campaign intent:', error);
        await shopify?.intents?.response?.error?.("Nudgesmart couldn't open that campaign.");
      } finally {
        if (!cancelled) {
          // Remove ONLY our own param — never setSearchParams({}), which would also drop
          // `action` and anything else the dashboard is mid-way through handling.
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete('nsCampaignId');
              return next;
            },
            { replace: true }
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams, shop?.id, shopify]);

  // Open modal after state update when creating new campaign
  useEffect(() => {
    if (shouldOpenModal && currentModalCampaignId && shopify?.modal) {
      // Need a small delay to ensure modal component has mounted
      const timer = setTimeout(() => {
        shopify.modal.show('my-modal');
        setShouldOpenModal(false);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [shouldOpenModal, currentModalCampaignId, shopify]);

  // Experiment management functions
  const createExperiment = async () => {
    try {
      // See ensureSessionId: this guard only became reachable when the session read stopped
      // suspending. Returning silently here is a dead button, so resolve or say why.
      const sessionId = await ensureSessionId();
      if (!sessionId) {
        console.error('No active session found');
        setBannerError({ message: 'Still connecting to Shopify. Try again in a moment.' });
        return;
      }

      // Get the first available template for default experience
      const defaultTemplate = templateData?.find(t => t.isDefault) || templateData?.[0];
      if (!defaultTemplate) {
        console.error('No templates available for creating experiment');
        return;
      }

      // Create the experiment directly without a campaign
      const newExperiment = await api.experiment.create({
        name: `New Experiment`,
        status: "draft",
        startDate: new Date(),
        shop: { _link: shop.id },
        createdBy: { _link: sessionId }
      });

      if (newExperiment?.id) {
        // Create default experiment segment for targeting
        const newSegment = await api.experiment_segment.create({
          name: `${newExperiment.name} Segment`,
          segmentRules: {
            conditions: {
              all: [{
                all: []
              }]
            },
            event: {
              params: {
                frequency: {
                  displayOn: 'everyPageView',
                  stopAfterUserAction: {
                    never: true
                  }
                }
              }
            }
          },
          shop: { _link: shop.id },
          createdBy: { _link: sessionId }
        });

        // Update experiment with segment reference
        if (newSegment?.id) {
          await api.experiment.update(newExperiment.id, {
            segment: { _link: newSegment.id }
          });
        }

        // Create a default control experience (experiment_variant)
        // `settings` is fetched by id — see fetchTemplateSettings.
        await api.experiment_variant.create({
          name: `Control Experience`,
          isControl: true,
          trafficWeight: 50,
          status: "active",
          settings: await fetchTemplateSettings(defaultTemplate.id, defaultTemplate.settings),
          experiment: { _link: newExperiment.id },
          campaignTemplate: { _link: defaultTemplate.id },
          shop: { _link: shop.id }
        });

        // Update state and refresh lists
        if (!hasAnyCampaigns) {
          setHasAnyCampaignsOverride(true);
        }

        // Trigger refresh for both lists
        setRefreshTrigger(Date.now());
        try {
          // No experiment list to refetch — <ExperimentResourceList> was only ever
          // mounted by renderExperimentsCard, which was never rendered. Removed with it.
          await campaignListRef.current?.refetchCampaigns();
        } catch (error) {
          console.error('Error refreshing lists after experiment creation:', error);
        }

        // Navigate to experiment page
        navigate(`/experiments/${newExperiment.id}`);
      }
    } catch (error) {
      /**
       * ⚠️ A `console.error` IS NOT AN ERROR STATE.
       *
       * This catch used to be console-only. It is the one catch in this file that survives
       * the production build — `console.error` is deliberately excluded from the
       * `esbuild.pure` list in vite.config.mjs, unlike log/warn/info/debug — which is
       * precisely what made it look adequate. It is not: nothing the MERCHANT can see
       * changes. The "Create experiment" button appears to do nothing, or worse, the
       * experiment row exists with no control variant because
       * `fetchTemplateSettings` threw after `api.experiment.create` succeeded.
       *
       * That throw is new and deliberate (see fetchTemplateSettings): a variant created with
       * `settings: {}` carries no displayType, position, teaser config or stopDisplaying, and
       * is broken in a way nobody notices until a shopper hits it. Failing loudly is the
       * point — so it has to actually reach the screen.
       */
      console.error('Error creating experiment:', error);
      setBannerError({
        heading: 'Experiment Creation Failed',
        message:
          error?.message ||
          'Failed to create the experiment. Please try again.'
      });
    }
  };




  
  const createCampaignFromTemplate = async (templateId) => {
    let campaignData = null; // Define in outer scope for error handling
    
    try {
      // Set the selected template ID first
      setSelectedTemplateId(templateId);
      
      // Find the template details
      // Optional-chained: the catalogue is now gated on `needsCatalogue`, so `templateData`
      // can legitimately be null. This path only runs from <TemplateLibraryComponent>'s
      // onSelectTemplate — i.e. the no-campaigns branch, where the gate is open — but the
      // `if (!template)` guard below already handles a miss, and a TypeError here would
      // take out the whole dashboard.
      const template = templateData?.find(t => t.id === templateId);

      if (!template) {
        console.error('Template not found');
        return;
      }

      // The list query above deliberately omits the element tree (see LIGHT SELECT note),
      // so fetch it for just this one template. This is REQUIRED, not an optimisation:
      // api/models/campaign/actions/create.js throws
      // "Campaign must have template and templateConfig fields" without it.
      //
      // Prefer the CDN copy when the template has been published — one immutable,
      // cacheable fetch instead of pulling multi-KB JSON columns. The tree must
      // end up in its PLAIN shape either way: campaign.create validates
      // `template.elements`, and the campaign row is the editor's authoring copy.
      // fetchPublishedDesign decodes; a null result means fall back to the DB.
      //
      // The `settings` blob is fetched alongside it, by id — see fetchTemplateSettings.
      // Started here so it overlaps the tree fetch instead of adding a serial hop.
      //
      // ⚠️ fetchTemplateSettings REJECTS now (it used to degrade to `{}`), and the `await`
      // for it is several awaits below. A promise that rejects before anything is listening
      // fires `unhandledrejection` — a page-level error, not a failed click, and one that
      // reports at the wrong place entirely. So mark it handled the instant it is created.
      //
      // The no-op `.catch` attaches a handler to `settingsPromise` ITSELF, which is what
      // silences the global handler; it returns a NEW promise that is deliberately dropped.
      // `settingsPromise` still rejects, so `await settingsPromise` below still throws into
      // the outer catch and the merchant still gets the banner. Do not reassign
      // `settingsPromise` to the result of the `.catch` — that would swallow the failure and
      // put the `settings: {}` bug straight back.
      const settingsPromise = fetchTemplateSettings(templateId, template.settings);
      settingsPromise.catch(() => {});

      const templateTree =
        (template?.payloadUrl ? await fetchPublishedDesign(template.payloadUrl) : null) ||
        (await api.campaign_template.findOne(templateId, {
          select: {
            template: true,
            templateConfig: true,
            templateVersion: true
          }
        }));

      const templateSettings = await settingsPromise;

      // Check if we have an active session.
      // See ensureSessionId: this guard was dead code while the session read suspended, and
      // is live now. A bare `return` here is a template tile that does nothing at all.
      const sessionId = await ensureSessionId();
      if (!sessionId) {
        console.error('No active session found');
        setBannerError({ message: 'Still connecting to Shopify. Try again in a moment.' });
        return;
      }

      // Add comprehensive validation and debugging
      // Dumps `shopId`, not the whole `shop` object: SHOP_SELECT now carries the
      // merchant's contact email (added for the Cal.com booking prefill), and
      // web/utils/logger.js:131 treats 'email' as a scrubbed field. This is DEV-CONSOLE
      // HYGIENE ONLY — `console.log` is in vite.config.mjs's `esbuild.pure` list, so the
      // call does not exist in production at all (see the note at :314-323 below). The
      // line that DOES survive production is the `console.error` at :1170.
      console.log('Campaign creation debug info:', {
        shopId: shop?.id,
        sessionId: sessionId,
        templateId: templateId,
        template: template
      });

      // Validate required IDs
      if (!shop?.id) {
        // ⚠️ `console.error` is deliberately EXCLUDED from vite.config.mjs's `esbuild.pure`
        // list, so unlike the console.log above this one SHIPS. Logging the whole `shop`
        // would put the merchant's contact email in the production browser console —
        // SHOP_SELECT started carrying `email` for the Cal.com booking prefill. Log the
        // shape instead; the id is the only thing this guard is actually asserting on.
        console.error('Shop or shop.id is missing:', { hasShop: !!shop, shopId: shop?.id });
        throw new Error('Shop context is required for campaign creation');
      }

      if (!templateId) {
        console.error('Template ID is missing:', templateId);
        throw new Error('Template ID is required for campaign creation');
      }
      
      // Check if this is the first campaign
      const isFirstCampaign = hasAnyCampaigns === false;
      
      if (isFirstCampaign) {
        // Set transitioning state to prevent flickering
        setIsTransitioning(true);
      }
      
      // Prepare campaign data for debugging
      campaignData = {
        name: `New ${template.name} Campaign`,
        status: "draft",
        type: template.campaign_type || "popup",
        priority: 50,
        settings: templateSettings,
        shop: { _link: shop.id },
        campaignTemplate: { _link: templateId },
        createdBy: { _link: sessionId }, 
        updatedBy: { _link: sessionId }
      };
      
      console.log('Campaign data being sent to API:', campaignData);
      
      // Use correct GraphQL field names with _link objects
      console.log('Values before API call:', {
        shopId: shop.id,
        templateId: templateId,
        sessionId: sessionId,
        shopIdType: typeof shop.id,
        templateIdType: typeof templateId,
        sessionIdType: typeof sessionId
      });
      
      // Use _link format with required audit fields
      const createParams = {
        name: `New ${template.name} Campaign`,
        status: "draft",
        type: template.campaign_type || "popup",
        priority: 50,
        settings: templateSettings,
        // Phase 3: Copy template data (copy-on-instantiation pattern)
        template: templateTree.template,
        templateConfig: templateTree.templateConfig,
        templateVersion: templateTree.templateVersion || template.templateVersion || "2.0.0",
        shop: { _link: shop.id },
        campaignTemplate: { _link: templateId },
        createdBy: { _link: sessionId },
        updatedBy: { _link: sessionId }
      };
      
      console.log('Final create params with _link objects (like other working examples):', createParams);
      
      const newCampaign = await api.campaign.create(createParams);
      
      // PREVIOUS ATTEMPTS (kept for reference):
      // EXPLICIT WRAPPING APPROACH:
      // const newCampaign = await api.campaign.create({
      //   campaign: campaignData  // Explicit wrapping
      // });
      // 
      // ORIGINAL _LINK APPROACH:
      // const newCampaign = await api.campaign.create(campaignData);


      
      // Open the modal with the new campaign ID
      if (newCampaign?.id) {

        localStorage.setItem('currentCampaignId', newCampaign.id);
        setCurrentModalCampaignId(newCampaign.id);
        
        // If this is the first campaign, we need to handle the transition
        if (isFirstCampaign) {
          // Update hasCampaigns first, then open modal after a short delay
          setHasCampaignsOverride(true);
          setHasAnyCampaignsOverride(true); // Also update hasAnyCampaigns
          
          // Give the component time to re-render with the dashboard view
          setTimeout(async () => {
            if (campaignListRef.current?.refetchCampaigns) {
              try {
                await campaignListRef.current.refetchCampaigns();
              } catch (error) {
                console.error('Error refreshing campaigns after creation:', error);
              }
            }
            openModal(newCampaign.id);
            setIsTransitioning(false);
          }, 300);
        } else {
          // Regular flow - just open the modal
          // Refetch campaigns list after creation
          try {
            await campaignListRef.current?.refetchCampaigns();
          } catch (error) {
            console.error('Error refreshing campaigns after creation:', error);
          }
          openModal(newCampaign.id, true);
        }
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      console.error('Campaign creation failed with full context:', {
        errorMessage: error.message,
        errorDetails: error,
        campaignData: campaignData,
        shopId: shop?.id,
        sessionId: session?.id,
        templateId: templateId
      });
      setIsTransitioning(false);
      
      // Show user-friendly error message
      let errorMessage = 'Failed to create campaign. Please try again.';
      
      // Check for GraphQL relationship errors (our fix target)
      if (error.message && error.message.includes('InternalBelongsToInput')) {
        errorMessage = 'Campaign creation failed due to data format issue. Please try again or contact support.';
        console.error('GraphQL BelongsToInput error detected - this is the relationship field issue we\'re fixing');
      }
      // Check if it's a billing limit error
      else if (error.message) {
        if (error.message.includes('limit reached')) {
          // Extract the detailed error message from the Gadget error
          const match = error.message.match(/campaigns limit reached \((\d+)\/(\d+)\)\. (.+)/);
          if (match) {
            errorMessage = `Campaign limit reached (${match[1]}/${match[2]}). ${match[3]}`;
          } else {
            errorMessage = 'Campaign limit reached. Please upgrade your plan to create more campaigns.';
          }

          // Track monetization friction for product analytics (GA4, prod-only).
          trackAdminUiEvent(api, ADMIN_UI_EVENTS.CAMPAIGN_LIMIT_REACHED, {
            current: match ? Number(match[1]) : undefined,
            limit: match ? Number(match[2]) : undefined,
          });

          // Set both banner and modal error for display
          setBannerError({
            message: errorMessage,
            action: {
              content: 'Upgrade Plan',
              onAction: () => {
                setBannerError(null);
                navigate('/billing');
              }
            }
          });
          
          setErrorModal({
            visible: true,
            title: 'Campaign Limit Reached',
            message: errorMessage,
            action: {
              content: 'Upgrade Plan',
              onAction: () => {
                setErrorModal({ visible: false, title: '', message: '', action: null });
                navigate('/billing');
              }
            }
          });
          
          // Scroll to top to show the error banner
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        } else if (error.message.includes('Shop context required')) {
          errorMessage = 'Unable to identify your shop. Please refresh the page and try again.';
        } else if (error.message.includes('plan')) {
          errorMessage = error.message;
        }
      }
      
      // Show error banner for other errors
      setBannerError({
        message: errorMessage
      });
      
      // Scroll to top to show the error banner
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Adapter for TemplateLibrary component — receives full template object, gates on billing
  const handleTemplateSelect = useCallback(async (template) => {
    // NULL-ONLY. A shop that is explicitly on the 'free' plan HAS a currentPlan, so this
    // check alone never caught it — the locked-type check below is what covers that case.
    if (!shop?.currentPlan) {
      navigate('/billing');
      return;
    }
    // Paid-plan-only campaign types (social proof today). Same expression as
    // SmartTriggerToggle.jsx:40; `shop` is guaranteed present by the guard above, so the
    // "undefined reads as free" trap does not apply on this path.
    const isFreePlan = !shop?.currentPlan || shop.currentPlan === 'free';
    if (isTemplateLocked(template?.campaign_type, isFreePlan)) {
      navigate('/billing');
      return;
    }
    await createCampaignFromTemplate(template.id);
  }, [shop?.currentPlan, navigate, createCampaignFromTemplate]);

  // Scroll to top whenever bannerError is set
  useEffect(() => {
    if (bannerError) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [bannerError]);

  /**
   * Enable the deferred (secondary) queries once the DASHBOARD HAS ACTUALLY PAINTED.
   *
   * ⚠️ "After initial render completes" is what the old `setTimeout(…, 500)` on `[]` deps
   * CLAIMED to do, and it could not: effects run on the FIRST COMMIT, and the first commit
   * on a cold load is the `<BootShell/>` at the render gate near the bottom of this file.
   * So the five deferred requests fired 500 ms after MOUNT — while the dashboard was still
   * gated — and competed with the very queries that have to land for it to paint. They are
   * the page's most expensive work: a 2-Shopify-GraphQL-call theme check, an up-to-8
   * round-trip count, 3 HogQL queries and the template catalogue view.
   *
   * The gate below mirrors the render gate's own terms, so this cannot fire before paint.
   * (`isTransitioning` is deliberately NOT included — it is an in-app campaign-create
   * transition, long after first paint, and gating on it would re-defer work already done.)
   *
   * ⚠️ `requestIdleCallback`, NOT a fixed delay — and that is not a micro-optimisation.
   * A re-gated `setTimeout(…, 500)` would REGRESS the first-run path: `needsCatalogue`
   * already requires `hasAnyCampaigns === false`, so the catalogue currently fires at
   * max(mount + 500 ms, campaigns-landed); a fixed re-gated delay makes it
   * campaigns-landed + 500 ms — up to half a second slower for the merchant seeing the app
   * for the very first time. Idle fires as soon as the main thread is quiet, which on that
   * path is immediately after the commit.
   *
   * ⚠️ `requestIdleCallback` does not exist in Safari ≤16, hence the setTimeout fallback —
   * kept SHORT (one paint's worth, not 500 ms) so the regression above stays bounded there
   * too. Same shape as the idle route warm just below, ChatWidget.jsx:110-112 and
   * useVisibleCards.js:282-285.
   */
  const paintGateOpen = hasAnyCampaigns !== null && !fetchingAnyCampaigns && !!shop;

  /**
   * Setup-call prompt — the proactive "I'll build your first campaign with you" offer.
   *
   * `paintGateOpen` is the right clock and the reason is structural, not probabilistic:
   * it requires `!!shop`, and ShopProvider's opaque boot cover is `isEarlyMount && !shop`
   * reading the SAME value, so the prompt cannot fire underneath the cover.
   */
  const isSetupCallSuppressed = useCallback(() => {
    // The App Bridge variant="max" editor fills the whole admin and is opened by the
    // ordinary first-campaign flow, well inside the deferral window.
    if (currentModalCampaignId) return true;
    // paintGateOpen deliberately EXCLUDES isTransitioning, so it must be checked here.
    if (isTransitioning) return true;
    if (errorModal.visible) return true;
    if (reviewModalShownRef.current) return true;

    // Any other <s-modal> already on screen. Two open at once is officially unsupported
    // by Shopify and, measured against the real polaris.js bundle, they simply STACK with
    // doubled backdrops and only the top one takes Escape.
    //
    // `document.querySelector('s-modal[open]')` returns null ALWAYS — `[open]` lives on
    // the <dialog> inside the shadow root, not on the host. Verified: the host selector
    // matched 0 while the shadow check correctly found 2.
    try {
      const others = Array.from(document.querySelectorAll("s-modal")).filter(
        (m) => m.id !== "setup-call-modal"
      );
      if (others.some((m) => m.shadowRoot?.querySelector("dialog")?.open === true)) {
        return true;
      }
    } catch {
      return true; // unreadable DOM -> assume busy rather than interrupt on a guess
    }
    return false;
  }, [currentModalCampaignId, isTransitioning, errorModal.visible]);

  const setupCall = useSetupCallPrompt({
    api,
    shopId: shop?.id,
    paintGateOpen,
    editorOpen: Boolean(currentModalCampaignId),
    isSuppressed: isSetupCallSuppressed,
  });

  useEffect(() => {
    if (!paintGateOpen) return undefined;

    let idleHandle = null;
    let fallbackTimer = null;

    const start = () => setShouldFetchSecondary(true);

    /*
     * ⚠️ THE TIMEOUT IS A CEILING ON A WRONG-STATE WINDOW, NOT A PERFORMANCE KNOB.
     *
     * One of the deferred requests is the theme-extension check, and <SetupGuide> is passed
     * `hasThemeExtension` with NO loading flag — it is `themeExtensionData?.enabled || false`,
     * so "unknown" and "not installed" are the same value to it. Until the check lands, a
     * merchant who HAS enabled the app embed is shown that step as incomplete, and a
     * fully-configured shop renders the whole card and then has it vanish (the card manages
     * its own visibility on completedCount === totalSteps). Longer deferral = a bigger, later
     * layout shift.
     *
     * 500 ms keeps the worst case at roughly the old `mount + 500 ms`, just anchored to paint
     * instead of mount. It is deliberately close to the Safari fallback below so the two
     * engines cannot diverge by an order of magnitude — at `timeout: 2000` the "degraded"
     * fallback path was actually 1.8 s FASTER than Chrome under load, which is backwards.
     */
    if (typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(start, { timeout: 500 });
    } else {
      fallbackTimer = setTimeout(start, 200);
    }

    return () => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
      if (idleHandle !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle);
      }
    };
  }, [paintGateOpen]);

  /**
   * Warm the CHEAP route chunks on idle, so the merchant's next click is instant.
   *
   * ONLY `integrations` (3.7 kB gz), `billing` (3.6 kB gz) and `survey-results`.
   *
   * ⛔ DO NOT ADD `analytics` HERE. It pulls the ~111 kB gz recharts vendor chunk, which
   * would spend more bandwidth speculatively than the entire dashboard costs. (Adding it
   * is gated on separate recharts work landing first.) And `templates` needs no warming —
   * it already shares its chunks with this route's own first-run branch.
   *
   * ⚠️ `survey-results` was listed under that same prohibition and did NOT belong there:
   * it does not touch recharts. Verified against the built chunk — its only static edges
   * are `index`, `vendor-react`, `vendor-polaris` and `campaignFilters`, and
   * components/SurveyResults.jsx imports just `react` and `../api`, drawing its charts as
   * hand-rolled divs. The prohibition on `analytics` is real; this half of it was not.
   *
   * ⚠️ `.finally`, NOT `.then` — a `.then` callback is skipped on rejection, so one flaky
   * chunk request would leave every route AFTER it in the chain un-warmed too, turning a
   * single harmless failure into three. Same shape and same reasoning as the module-eval
   * warm-ups in components/App.jsx.
   *
   * ⚠️ NOT AT MODULE SCOPE, and not inside the editor realm. `requestIdleCallback` does not
   * exist in Safari ≤16, hence the setTimeout fallback. This is a JS-side warm on purpose:
   * `vite-plugins/ns-critical-assets.mjs` deliberately does not walk `dynamicImports`
   * ("preloading them would undo the code splitting we rely on"), so a `modulepreload`
   * would fight that decision.
   */
  /*
   * ⚠️ GATED ON `paintGateOpen` FOR THE SAME REASON THE SECONDARY-QUERY EFFECT ABOVE IS.
   *
   * This used to have `[]` deps, which meant it scheduled on the FIRST COMMIT — and on a cold
   * load the first commit is the <BootShell/>. `requestIdleCallback` fires when the MAIN
   * THREAD is quiet, and the main thread is idle precisely while the campaigns query is in
   * flight over the network. So three speculative route chunks could start downloading ahead
   * of the query the render gate is actually waiting on — bandwidth competing with paint.
   *
   * Deferring the DATA work above while leaving the SPECULATIVE work early would have
   * inverted the whole point of that change.
   */
  useEffect(() => {
    if (IS_CAMPAIGN_EDITOR || !paintGateOpen) return undefined;

    let idleHandle = null;
    let fallbackTimer = null;

    const warm = () => {
      import("./integrations")
        .catch(() => {})
        .finally(() => {
          import("./billing")
            .catch(() => {})
            .finally(() => {
              import("./survey-results").catch(() => {});
            });
        });
    };

    if (typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(warm, { timeout: 4000 });
    } else {
      fallbackTimer = setTimeout(warm, 2000);
    }

    return () => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
      if (idleHandle !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle);
      }
    };
  }, [paintGateOpen]);

  // Handle create experiment action from query params
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'create-experiment' && shop?.id && session?.id && templateData) {
      // Clear the query param and create experiment
      setSearchParams({});
      createExperiment();
    }
  }, [searchParams, setSearchParams, shop?.id, session?.id, templateData]);

  const handleCancelCampaign = () => {
    // Send CANCEL_REQUESTED message to modal to check for unsaved changes
    const modal = document.getElementById('my-modal');
    modal?.contentWindow?.postMessage({ type: 'CANCEL_REQUESTED' }, location.origin);
    // The modal will respond with CONFIRM_CANCEL if user confirms (either after saving or discarding)
    // The actual close logic will happen in the CONFIRM_CANCEL message handler
  };

  // Phase 1: open AI Co-Pilot from the App Bridge TitleBar (template-level entry).
  // popup.jsx handleMessage routes OPEN_AI_COPILOT to PreviewContainer.openAiCoPilot()
  // which switches the right-sidebar to the AI tab in whole-campaign mode (selectedElement=null).
  const handleOpenAiCoPilot = () => {
    const modal = document.getElementById('my-modal');
    modal?.contentWindow?.postMessage({ type: 'OPEN_AI_COPILOT' }, location.origin);
  };

  // Always render the modal at the root of the component
  const modalSrc = currentModalCampaignId && currentModalCampaignId.trim() !== '' 
    ? `/popup?campaignId=${encodeURIComponent(currentModalCampaignId)}` 
    : undefined;
  
  // Only render modal when we have a valid src, and use key to force re-mount
  const myModal = modalSrc ? (
    <Modal 
      key={`modal-${currentModalCampaignId}`} 
      id="my-modal" 
      variant="max" 
      src={modalSrc}
    >
      <div></div>
      <TitleBar title="Campaign Editor">
        <button onClick={handleOpenAiCoPilot} disabled={isSaving}>AI Co-Pilot</button>
        <button
          variant="primary"
          loading={isSaving ? '' : undefined}
          disabled={isSaving}
          onClick={() => handleSaveCampaign()}
        >Save</button>
        <button onClick={handleCancelCampaign} disabled={isSaving}>Cancel</button>
      </TitleBar>
    </Modal>
  ) : null;

  /**
   * The setup-call modal. Rendered as a value alongside errorModalComponent because that
   * value is already threaded through EVERY early-return branch of this route — mounting
   * it inside one branch only would make it vanish whenever the route re-renders into a
   * skeleton or an empty state.
   *
   * `<LazyErrorBoundary fallback={...}>` is NOT used here: this is an optional widget, and
   * a chunk failure must not paint a critical banner over a working dashboard. The Suspense
   * fallback is null for the same reason.
   */
  const setupCallModalComponent = shop ? (
    <Suspense fallback={null}>
      <SetupCallModal
        open={setupCall.open}
        onClose={setupCall.dismiss}
        onBooked={setupCall.onBooked}
        variant={setupCall.eligibility?.variant}
        trialDaysRemaining={setupCall.eligibility?.trialDaysRemaining}
        shop={shop}
      />
    </Suspense>
  ) : null;

  // Error modal for campaign limits and other errors (lazy-loaded, only needed for rare error cases)
  const errorModalComponent = (
    <Suspense fallback={null}>
      <ErrorModal
        isVisible={errorModal.visible}
        title={errorModal.title}
        message={errorModal.message}
        action={errorModal.action}
        onClose={() => setErrorModal({ visible: false, title: '', message: '', action: null })}
      />
    </Suspense>
  );

  /**
   * The campaigns query failed and left us with nothing to render.
   *
   * ⚠️ THIS MUST SIT ABOVE THE SKELETON GATE. "Still loading" and "broken" are
   * indistinguishable to the person watching, and the derivation deliberately does
   * NOT fold an error into `false` — so without this branch a failed query would
   * hold `hasAnyCampaigns === null` and spin forever. Same shape, and same reason,
   * as the settled-but-empty branch in providers/ShopProvider.jsx.
   *
   * Guarded on `!anyCampaignsData` so a background refetch failure never tears down
   * a dashboard that is already showing good data — `live: true` and urql's
   * cache-and-network default both re-issue this query after first paint.
   */
  if (anyCampaignsError && !anyCampaignsData && hasAnyCampaignsOverride === null) {
    return (
      <>
        {myModal}
        {errorModalComponent}
        {setupCallModalComponent}
        <s-page heading="We couldn't load your campaigns" inlineSize="large">
          <s-section>
            <s-stack direction="block" gap="base">
              <s-banner tone="critical" heading="Something went wrong">
                <s-text>
                  {anyCampaignsError?.message ??
                    "We couldn't load your campaigns. Reload to try again."}
                </s-text>
              </s-banner>
              <s-stack direction="inline" justifyContent="start">
                <s-button variant="primary" onClick={() => window.location.reload()}>
                  Try again
                </s-button>
              </s-stack>
            </s-stack>
          </s-section>
        </s-page>
      </>
    );
  }

  /**
   * ⚠️ `!shop` IS PART OF THIS GATE AND IS LOAD-BEARING — do not drop it as redundant
   * with ShopProvider's own shop gate.
   *
   * ShopProvider now mounts "/" EARLY (EARLY_MOUNT_PATHS), so this route renders while
   * the shop query is still in flight — that is the entire point: the campaigns query
   * above keys off the session's shopId and therefore no longer waits for it. The two
   * queries genuinely race (the shop query is `live: true`, i.e. a subscription, and can
   * settle after a plain query issued later), so `shop` can still be undefined when the
   * campaigns answer arrives.
   *
   * Everything below tolerates that — every `shop` read on the render path is optional-
   * chained — but tolerating it is not the same as wanting it. Without this term the
   * dashboard would commit a full first render against empty shop data: an empty
   * myshopifyDomain in <SetupGuide>, a null trialDaysRemaining, the `!shop?.currentPlan`
   * upgrade banner shown to a paying merchant — or, if `hasAnyCampaigns` resolved false
   * in that window, the entire first-run Welcome page and its template picker mounted and
   * thrown away a tick later.
   *
   * It cannot strand the page: a shop query that settles empty never reaches here at
   * all — ShopProvider's `!(isEarlyMount && fetchingShop)` guard falls into its own
   * terminal "We couldn't load your store" branch and this route unmounts.
   *
   * Nothing is lost by waiting here. The merchant is looking at ShopProvider's cover
   * until `shop` lands anyway; what the early mount buys is the campaigns round trip
   * happening DURING that window instead of after it, so this gate lifts the moment the
   * shop arrives rather than a whole query later.
   */
  if (hasAnyCampaigns === null || fetchingAnyCampaigns || isTransitioning || !shop) {
    return (
      <>
        {myModal}
        {errorModalComponent}
        {setupCallModalComponent}
        <BootShell variant="dashboard" />
      </>
    );
  }

  if (hasAnyCampaigns === false) {
    return (
      <>
        {myModal}
        {errorModalComponent}
        {setupCallModalComponent}
        <s-page heading="Welcome to Nudgesmart" inlineSize="large">
          <s-stack direction="block" gap="base">
            {bannerError && (
              <s-banner heading={bannerError.heading || 'Campaign Creation Failed'} tone="critical" dismissible onDismiss={() => setBannerError(null)}>
                {bannerError.message}
                {bannerError.action && (
                  <s-button slot="secondary-actions" variant="secondary" onClick={bannerError.action.onAction}>
                    {bannerError.action.content}
                  </s-button>
                )}
              </s-banner>
            )}
            <PopupsPausedBanner count={popupCount} limit={popupLimit} onUpgrade={() => navigate('/billing')} />
            {/* Start Free Trial Banner - Show for users without a plan */}
            {!shop?.currentPlan && (
              <s-banner heading="Start Your Free Trial" tone="info">
                Select a plan to unlock campaign creation. All plans include a 14-day free trial -
                no charge until the trial ends. Cancel anytime.
                <s-button slot="secondary-actions" variant="secondary" onClick={() => navigate('/billing')}>
                  Choose Your Plan
                </s-button>
              </s-banner>
            )}

            {/* TOP SECTION: Two-column for compact info cards */}
            <s-query-container>
              <s-grid gridTemplateColumns="@container (inline-size > 720px) 2fr 1fr, 1fr" gap="base">
                <s-stack direction="block" gap="base">
                  {/* Trial Status - Show only AFTER plan is selected */}
                  {showTrialStatus && (
                    <TrialStatus
                      trialStartedAt={shop.trialStartedAt}
                      totalTrialDays={shop.trialDays || 14}
                      isPaidUser={shop?.subscriptionStatus === 'active'}
                      leadsCaptured={trialConversions}
                      totalConversions={trialConversions}
                      conversionRate={0}
                    />
                  )}

                  {/* Setup Guide */}
                  <SetupGuide
                    inputsSettled={setupInputsSettled}
                    hasThemeExtension={hasThemeExtension}
                    hasAnyCampaigns={false}
                    hasActiveCampaign={hasActiveCampaign}
                    hasEmailIntegration={hasEmailIntegration}
                    myshopifyDomain={shop?.myshopifyDomain || ''}
                    onRefreshExtensionStatus={handleRefreshExtensionStatus}
                    onTemplateSelect={() => {
                      const templateSection = document.getElementById('template-selection');
                      if (templateSection) {
                        templateSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  />
                </s-stack>

                {/* Sidebar - What's New + Support */}
                <s-stack direction="block" gap="base">
                  <WhatsNew
                    entries={changelogData}
                    maxEntries={5}
                    compact
                  />

                  <SupportHub
                    shopName={shop?.name || ''}
                    shopOwner={shop?.shopOwner || ''}
                    shopEmail={shop?.email || ''}
                    shopId={shop?.id || ''}
                    shopDomain={shop?.myshopifyDomain || ''}
                    onBookClick={setupCall.openManually}
                    subscriptionPlan={shop?.currentPlan || 'trial'}
                    trialDaysRemaining={shop?.trialStartedAt ? Math.max(0, (shop.trialDays || 14) - Math.floor((new Date() - new Date(shop.trialStartedAt)) / (1000 * 60 * 60 * 24))) : null}
                    compact
                  />
                </s-stack>
              </s-grid>
            </s-query-container>

            {/* BOTTOM SECTION: Full-width for template selection */}
            <div id="template-selection">
              <Suspense fallback={
                <s-section>
                  <s-stack direction="block" gap="base">
                    <SkeletonDisplayText size="small" />
                    <s-grid gridTemplateColumns="repeat(3, 1fr)" gap="base">
                      {[1, 2, 3].map(i => (
                        <s-section key={i}>
                          <SkeletonBodyText lines={6} />
                        </s-section>
                      ))}
                    </s-grid>
                  </s-stack>
                </s-section>
              }>
                <TemplateLibraryComponent
                  onSelectTemplate={handleTemplateSelect}
                  title="Choose Your Campaign Template"
                  subtitle="Select from our proven templates to create high-converting campaigns in minutes"
                />
              </Suspense>
            </div>

            {/* REMOVED — the "Looking for more options? / Browse all templates →" footer.
                The embedded library above it now carries its own search, industry tabs,
                format facets and pagination, so a trailing link to a fuller version of the
                thing the merchant is already using is a dead end rather than an escape
                hatch. Left navigation to /templates lives in the nav. Commented rather
                than deleted so it can come back if the embedded library is ever cut down.
            <s-section>
              <s-stack direction="inline" justifyContent="center" gap="small-200">
                <s-text color="subdued">Looking for more options?</s-text>
                <s-button onClick={() => navigate('/templates')} variant="tertiary">
                  Browse all templates →
                </s-button>
              </s-stack>
            </s-section>
            */}
          </s-stack>
        </s-page>
      </>
    );
  }

  return (
    <>
      {myModal}
      {errorModalComponent}
        {setupCallModalComponent}
      <s-page heading="Nudgesmart Dashboard" inlineSize="large">
        <s-stack direction="block" gap="base">
          {bannerError && (
            <s-banner heading={bannerError.heading || 'Campaign Creation Failed'} tone="critical" dismissible onDismiss={() => setBannerError(null)}>
              {bannerError.message}
              {bannerError.action && (
                <s-button slot="secondary-actions" variant="secondary" onClick={bannerError.action.onAction}>
                  {bannerError.action.content}
                </s-button>
              )}
            </s-banner>
          )}
          <PopupsPausedBanner count={popupCount} limit={popupLimit} onUpgrade={() => navigate('/billing')} />

          <s-query-container>
            <s-grid gridTemplateColumns="@container (inline-size > 720px) 2fr 1fr, 1fr" gap="base">
              {/* Main Content (2/3) */}
              <s-stack direction="block" gap="base">
                {/* Trial Status - Show only AFTER plan is selected */}
                {showTrialStatus && (
                  <TrialStatus
                    trialStartedAt={shop.trialStartedAt}
                    totalTrialDays={shop.trialDays || 14}
                    isPaidUser={shop?.subscriptionStatus === 'active'}
                    leadsCaptured={trialConversions}
                    totalConversions={trialConversions}
                    conversionRate={0}
                  />
                )}

                {/* Analytics Summary — `shouldFetchSecondary` now gates only the RENDER.
                    The NETWORK left this gate: the request is started unconditionally from
                    the prefetch effect far above, on `effectiveShopId`. What stays deferred
                    is the card's lazy chunk and its paint, which is main-thread work rather
                    than latency — so the first-paint budget is unchanged while the data is
                    usually already in hand by the time the card mounts.
                    (This comment used to justify the gate by the request's cost: "7 parallel
                    promises → 9 HogQL queries". That was the FULL insight set; the card asks
                    for `summary=true`, which the route answers with 3 — measured at ~180 ms
                    of PostHog work inside a ~670 ms request whose floor is ~490 ms of Gadget
                    route overhead. The cost was never what made the card slow.)
                    The skeleton is the same one the Suspense fallback shows, so the card's
                    slot is reserved either way and nothing shifts when it arrives. */}
                <Suspense fallback={<s-section><SkeletonBodyText lines={3} /></s-section>}>
                  {shouldFetchSecondary
                    ? <AnalyticsSummaryCard
                        /* ⚠️ KEYED ON THE SHOP ID. The card seeds its state from the
                           prefetch in a useState initialiser, which runs on the FIRST
                           render only — so on a shop change it would keep painting the
                           previous shop's impressions/revenue, unlabelled, until the new
                           response landed. Remounting resets the whole state machine and
                           re-runs the seed. One line instead of reset plumbing inside the
                           component. (The module cache is already shop-keyed; this closes
                           the display window, not a cache leak.) */
                        key={effectiveShopId}
                        shopId={effectiveShopId}
                        /* Injected, NOT imported by the card — importing
                           utils/analyticsSummary.js there makes it a module shared between
                           this dynamic-entry chunk and its own lazy child, which strips
                           this chunk's facadeModuleId and fails the
                           vite-plugins/ns-critical-assets.mjs preload guard. See the
                           WARNING at the top of AnalyticsSummaryCard.jsx. */
                        fetchSummary={fetchAnalyticsSummary}
                        readSummary={readAnalyticsSummary}
                      />
                    : <s-section><SkeletonBodyText lines={3} /></s-section>}
                </Suspense>

                {/* Setup Guide - Component manages its own visibility via completedCount === totalSteps */}
                <SetupGuide
                  inputsSettled={setupInputsSettled}
                  hasThemeExtension={hasThemeExtension}
                  hasAnyCampaigns={true}
                  hasActiveCampaign={hasActiveCampaign}
                  hasEmailIntegration={hasEmailIntegration}
                  myshopifyDomain={shop?.myshopifyDomain || ''}
                  onRefreshExtensionStatus={handleRefreshExtensionStatus}
                  compact
                />

                {/* AI Insights (Wave 2) — Gemini-generated daily suggestions.
                    Card self-hides until real insights exist; fallback is null so
                    no skeleton flashes during the lazy chunk load for empty shops.

                    GATED behind `shouldFetchSecondary`, matching <AnalyticsSummaryCard/>
                    above. It was previously rendered unconditionally while its own query
                    carried a discarded `enabled` third argument, so it fired pre-auth on
                    first render — measured at 4105ms returning ZERO rows on production.

                    `shopId` is REQUIRED: the query is `pause: !shopId`, so omitting it
                    leaves the card permanently paused and silently invisible. Pass
                    `effectiveShopId` (session-derived) rather than `shop?.id` so it does
                    not wait on the shop query. */}
                {shouldFetchSecondary && (
                  <Suspense fallback={null}>
                    <InsightsCard shopId={effectiveShopId} />
                  </Suspense>
                )}

                {/* Active Campaigns */}
                <s-section>
                  <s-stack direction="block" gap="base">
                    <s-stack direction="inline" justifyContent="space-between" alignItems="center">
                      <s-heading>Active Campaigns</s-heading>
                      <s-button
                        variant="primary"
                        icon="plus"
                        onClick={() => navigate('/templates')}
                      >
                        Create Campaign
                      </s-button>
                    </s-stack>
                    <Suspense fallback={<s-section><SkeletonBodyText lines={4} /></s-section>}>
                      <CampaignResourceList
                        ref={campaignListRef}
                        shopId={shop?.id}
                        refreshTrigger={refreshTrigger}
                        onRefresh={() => setRefreshTrigger(Date.now())}
                        onAllDeleted={() => {
                          // Clear BOTH, back to "no override" — see the derivation comment.
                          // Writing `false` here would leave hasAnyCampaignsOverride pinned
                          // `true` from a preceding create, stranding the merchant on an
                          // empty dashboard that can never return to the Welcome page.
                          setHasCampaignsOverride(null);
                          setHasAnyCampaignsOverride(null);
                        }}
                        onEditCampaign={openModal}
                      />
                    </Suspense>
                  </s-stack>
                </s-section>
              </s-stack>

              {/* Sidebar (1/3) - What's New + Support + Campaign Summary stacked */}
              <s-stack direction="block" gap="base">
                <WhatsNew
                  entries={changelogData}
                  maxEntries={5}
                  compact
                />

                <SupportHub
                  shopName={shop?.name || ''}
                  shopOwner={shop?.shopOwner || ''}
                  shopEmail={shop?.email || ''}
                  shopId={shop?.id || ''}
                  shopDomain={shop?.myshopifyDomain || ''}
                  onBookClick={setupCall.openManually}
                  subscriptionPlan={shop?.currentPlan || 'trial'}
                  trialDaysRemaining={shop?.trialStartedAt ? Math.max(0, (shop.trialDays || 14) - Math.floor((new Date() - new Date(shop.trialStartedAt)) / (1000 * 60 * 60 * 24))) : null}
                  compact
                />

                {/* Campaign Summary */}
                <s-section>
                  <s-stack direction="block" gap="base">
                    <s-heading>Campaign Summary</s-heading>
                    <s-stack direction="block" gap="small">
                      <s-stack direction="inline" justifyContent="space-between">
                        <s-text>Active Campaigns</s-text>
                        <s-badge tone="success">{String(campaignCounts.active)}</s-badge>
                      </s-stack>
                      <s-divider />
                      <s-stack direction="inline" justifyContent="space-between">
                        <s-text>Draft Campaigns</s-text>
                        <s-badge>{String(campaignCounts.draft)}</s-badge>
                      </s-stack>
                      <s-divider />
                      <s-stack direction="inline" justifyContent="space-between">
                        <s-text>Total Campaigns</s-text>
                        <s-text type="strong">{String(campaignCounts.total)}</s-text>
                      </s-stack>
                    </s-stack>
                  </s-stack>
                </s-section>
              </s-stack>
            </s-grid>
          </s-query-container>

          {/* Full-width: Email Integration CTA - Only show if not connected */}
          {!hasEmailIntegration && (
            <s-section>
              <s-stack direction="block" gap="base">
                <s-heading>Connect Your Email Provider</s-heading>
                <s-paragraph>Sync captured emails directly to Klaviyo.</s-paragraph>
                <s-stack direction="block" alignItems="end">
                  <s-button onClick={() => navigate('/integrations')} variant="primary">
                    Manage Integrations
                  </s-button>
                </s-stack>
              </s-stack>
            </s-section>
          )}
        </s-stack>
      </s-page>
    </>
  );
};
